import { useEffect, useState, useRef } from 'react';

import { Severity, ErrorType } from '@atlassian/growth-commons-error-analytics';
import type { StorageAdapter } from '@atlassian/upgrade-free-pool-target-experiment';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { useExperimentErrorAnalytics } from '../utils/useExperimentErrorAnalytics.ts';

const UPFT_COHORT_LOCAL_STORAGE_KEY = 'atl-UPFTConfluenceStandardOfferCohort';

export const useUpgradeFreePoolTargetExperimentCohort = ({
	isEligible,
	analyticsAttributes,
	confluenceStorageAdapter,
}: {
	isEligible?: boolean;
	analyticsAttributes: Record<string, any>;
	confluenceStorageAdapter: StorageAdapter;
}) => {
	const { trackError, fireExperimentExposureEvent } = useExperimentErrorAnalytics({
		defaultAdditionalAttributes: analyticsAttributes,
		experimentKey: 'twcg-206_confluence_upgrade_permanent_free',
	});

	const [abCohort, setABCohort] = useState<string>('not-enrolled');

	// Track if analytics events have been fired to prevent duplicates
	const abEventFiredRef = useRef<boolean>(false);

	useEffect(() => {
		if (isEligible && fg('twcg-206_confluence_upgrade_permanent_free_fg')) {
			try {
				setABCohort(expVal('twcg-206_confluence_upgrade_permanent_free', 'cohort', 'not-enrolled'));
				confluenceStorageAdapter.set(
					UPFT_COHORT_LOCAL_STORAGE_KEY,
					expVal('twcg-206_confluence_upgrade_permanent_free', 'cohort', 'not-enrolled'),
				);
			} catch (error) {
				trackError({
					errorType: ErrorType.GENERIC,
					errorMessage:
						error instanceof Error ? error.message : 'Experiment cohort determination failed',
					severity: Severity.MEDIUM,
					source: 'useUpgradeFreePoolTargetExperimentCohort_cohortDetermination',
					cohort: 'not-enrolled',
					additionalAttributes: {
						isEligible,
						featureGateEnabled: fg('twcg-206_confluence_upgrade_permanent_free_fg'),
						errorStack: error instanceof Error ? error.stack : undefined,
						errorName: error instanceof Error ? error.name : undefined,
					},
				});
				// Fall back to not-enrolled on error
				setABCohort('not-enrolled');
			}
		}
	}, [isEligible, trackError, confluenceStorageAdapter]);

	useEffect(() => {
		if (abCohort !== 'not-enrolled' && !abEventFiredRef.current) {
			const success = fireExperimentExposureEvent({
				cohort: abCohort,
				experimentId: 'twcg-206_confluence_upgrade_permanent_free',
				actionSubjectId: 'twcg-206_confluence_upgrade_permanent_free_exposed',
				source: 'twcg-206_confluence_upgrade_permanent_free',
			});
			if (success) {
				abEventFiredRef.current = true;
			}
		}
	}, [abCohort, fireExperimentExposureEvent]);

	return { abCohort };
};
